import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Check, CheckCircle2, ChevronLeft, ChevronRight, Copy } from "lucide-react";
import type { HandDetector } from "@tensorflow-models/hand-pose-detection";
import type { ObjectDetection } from "@tensorflow-models/coco-ssd";
import MobileCameraOverlay, {
  type MobileCameraDetectionSnapshot,
} from "@/components/interview/MobileCameraOverlay";
import SlideFour from "../assets/mobile-webcam/Slide-4.mp4";
import DontPickItUp from "../assets/mobile-webcam/DontPickItUp.mp4";

interface MobileWebCamAfterConnectionProps {
  remoteStream: MediaStream | null;
  handDetector?: HandDetector | null;
  objectDetector?: ObjectDetection | null;
  detectionError?: Error | null;
  detectionStatusMessage?: string;
  isDetectionPreparing?: boolean;
  onProceed: () => void;
  hasProceeded?: boolean;
  connectionUrl?: string;
  footer?: React.ReactNode;
}

const slides = [
  {
    video: SlideFour,
    title: "Position Your Phone",
    details: ["Prop against water bottle or stack of books", "Position 1-2 feet away from laptop"],
    captureText: "Your phone camera should capture:",
    captureItems: ["Your laptop screen", "Your hands on keyboard"],
  },
  {
    video: DontPickItUp,
    title: "Keep It Stable",
    subHeading: "Important: Your phone will monitor automatically",
    details: ["Don't pick up phone"],
    paragraph:
      "Once positioned, your phone will monitor automatically throughout the interview. Moving or touching it may trigger alerts.",
  },
];

const MobileWebCamAfterConnection: React.FC<MobileWebCamAfterConnectionProps> = ({
  remoteStream,
  handDetector,
  objectDetector,
  detectionError,
  detectionStatusMessage,
  isDetectionPreparing,
  onProceed,
  hasProceeded = false,
  connectionUrl,
  footer,
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isCopied, setIsCopied] = useState(false);
  const [detectionSnapshot, setDetectionSnapshot] = useState<MobileCameraDetectionSnapshot | null>(null);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);

  const handsDetected = (detectionSnapshot?.handsDetected ?? 0) > 0;
  const laptopDetected = Boolean(detectionSnapshot?.workspaceDetected);
  const detectionRunning = detectionSnapshot?.detectionRunning ?? false;
  const detectionWarning = detectionSnapshot?.warning ?? null;
  const handsStatus = detectionSnapshot?.status?.hands ?? (handsDetected ? "Hands detected" : "Waiting for hands…");
  const workspaceStatus =
    detectionSnapshot?.status?.workspace ?? (laptopDetected ? "Workspace detected" : "Adjust phone to capture laptop");
  const isReadyToProceed = useMemo(
    () => Boolean(remoteStream && handsDetected && laptopDetected),
    [remoteStream, handsDetected, laptopDetected],
  );
  const proceedButtonLabel = hasProceeded ? "Mobile Connected" : "Proceed";
  const showPreparingOverlay =
    Boolean(remoteStream) && !detectionError && (isDetectionPreparing || !detectionRunning);

  useEffect(() => {
    if (!remoteStream) {
      setDetectionSnapshot(null);
    }
  }, [remoteStream]);

  useEffect(() => {
    videoRefs.current.forEach((video, index) => {
      if (!video) return;
      if (index === currentSlide) {
        video.play().catch(() => undefined);
      } else {
        video.pause();
        video.currentTime = 0;
      }
    });
  }, [currentSlide]);

  const handleDetectionUpdate = useCallback((snapshot: MobileCameraDetectionSnapshot) => {
    setDetectionSnapshot(snapshot);
  }, []);

  const handleCopyLink = async () => {
    if (!connectionUrl) return;
    try {
      await navigator.clipboard.writeText(connectionUrl);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      console.error("Failed to copy link:", error);
    }
  };


  return (
    <div className="flex flex-row gap-7 w-full items-stretch">
        {/* Left Column - Instructions */}
        <div className="flex-[6.5] flex flex-col bg-white rounded-xl border border-[#0000001A] p-8 relative">
          {/* Sliding Video Section */}
          <div className="overflow-hidden rounded-xl w-full">
            <div
              className="flex transition-transform duration-500 ease-in-out w-full"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {slides.map((slide, index) => (
                <div
                  key={index}
                  className="w-full min-w-full max-w-full flex-shrink-0"
                >
                  <div className="w-full h-[260px] bg-[#E0E0DF] rounded-md overflow-hidden">
                    <video
                      ref={(el) => {
                        videoRefs.current[index] = el;
                      }}
                      src={slide.video}
                      className="w-full h-full object-cover"
                      loop={index !== 0}
                      muted
                      playsInline
                      onEnded={() => {
                        if (index === 0 && currentSlide === 0) {
                          setCurrentSlide(1);
                        }
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Controls - Fixed after video */}
          <div className="flex items-center justify-between gap-4 mt-5">
            <div className="flex gap-4">
              {slides.map((_, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setCurrentSlide(idx)}
                  className={`h-1 w-16 rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#1559FC] ${
                    idx === currentSlide
                      ? "bg-[#001630]"
                      : "bg-[#EAEAEA] hover:bg-[#D5D5D5]"
                  }`}
                  aria-label={`Go to slide ${idx + 1}`}
                  aria-pressed={idx === currentSlide}
                />
              ))}
            </div>

            <div className="flex items-center gap-2">
              {currentSlide > 0 && (
                <button
                  onClick={() => setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length)}
                  className="w-7 h-7 bg-[#F4F5F5] rounded-md flex items-center justify-center hover:bg-[#E5E5E5] transition-colors"
                >
                  <ChevronLeft className="w-5 h-5 text-[#414447]" />
                </button>
              )}
              {currentSlide < slides.length - 1 && (
                <button
                  onClick={() => setCurrentSlide((prev) => (prev + 1) % slides.length)}
                  className="w-7 h-7 bg-[#F4F5F5] rounded-md flex items-center justify-center hover:bg-[#E5E5E5] transition-colors"
                >
                  <ChevronRight className="w-5 h-5 text-[#414447]" />
                </button>
              )}
            </div>
          </div>

          {/* Sliding Content Section */}
          <div className="overflow-hidden rounded-xl w-full mt-3 flex-1 flex flex-col">
            <div
              className="flex transition-transform duration-500 ease-in-out w-full flex-1"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {slides.map((slide, index) => (
                <div
                  key={index}
                  className="flex flex-col w-full min-w-full max-w-full gap-6 relative flex-shrink-0 h-full"
                >
                  <div className="flex flex-col gap-[2px]">
                    <h3 className="text-[18px] font-ubuntu-sans font-medium text-[#414447] leading-[24px]">
                      {slide.title}
                    </h3>
                    {slide.subHeading && (
                      <p className="text-sm font-normal text-[#686B6D] leading-[21px] mt-[2px]">
                        {slide.subHeading}
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col gap-[5px]">
                    {slide.details.map((detail, idx) => (
                      <p
                        key={idx}
                        className="text-sm text-[#686B6D] leading-[21px]"
                      >
                        {detail.startsWith("-") ? detail : `• ${detail}`}
                      </p>
                    ))}
                  </div>

                  {slide.captureText && (
                    <>
                      <p className="text-sm text-[#686B6D] leading-[21px]">
                        {slide.captureText}
                      </p>
                      <div className="flex flex-col gap-[5px]">
                        {slide.captureItems?.map((item, idx) => (
                          <p
                            key={idx}
                            className="text-sm text-[#686B6D] leading-[21px]"
                          >
                            • {item}
                          </p>
                        ))}
                      </div>
                    </>
                  )}

                  {slide.paragraph && (
                    <p className="text-sm text-[#686B6D] leading-[21px]">
                      {slide.paragraph}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Copy Connection Link Button */}
          {connectionUrl && (
            <button
              onClick={handleCopyLink}
              className="mt-4 text-xs text-[#030213] hover:text-[#414447] transition-colors flex items-center gap-1.5 font-medium self-end"
            >
              {isCopied ? (
                <>
                  <Check className="h-3.5 w-3.5" />
                  <span>Link Copied!</span>
                </>
              ) : (
                <>
                  <Copy className="h-3.5 w-3.5" />
                  <span>Copy Connection Link</span>
                </>
              )}
            </button>
          )}
        </div>

        <div className="flex-[3.5] flex flex-col gap-4">
          <div className="relative w-full h-[400px] bg-black rounded-[10px] border-2 border-[#364153] overflow-hidden">
            <div className="absolute top-3 left-3 flex items-center gap-2.5 px-2 py-1 bg-black rounded z-10">
              <span className="text-white text-sm leading-5">
                Mobile Device (Workspace Camera)
              </span>
              <div className="w-2 h-2 bg-[#00C950] rounded-full" />
            </div>
            <div className="w-full h-full overflow-hidden">
              {remoteStream ? (
                <div className="w-full h-[167%] -mt-[67%]">
                  <MobileCameraOverlay
                    stream={remoteStream}
                    handDetector={handDetector ?? null}
                    objectDetector={objectDetector ?? null}
                    detectionError={detectionError ?? null}
                    enableDetections={!detectionError}
                    flipHorizontal={false}
                    drawVideo={false}
                    containerClassName="h-full w-full"
                    videoClassName="h-full w-full object-cover"
                    canvasClassName="h-full w-full"
                    onDetectionUpdate={handleDetectionUpdate}
                    mode="check-setup"
                  />
                  {showPreparingOverlay && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/70 text-center text-white">
                      <div className="mb-3 h-10 w-10 animate-spin rounded-full border-b-2 border-white" />
                      <p className="text-sm font-medium">
                        {isDetectionPreparing ? "Preparing detection models…" : "Starting detection…"}
                      </p>
                      <p className="mt-1 text-xs opacity-80">
                        {detectionStatusMessage || "Hold steady while we verify your setup."}
                      </p>
                    </div>
                  )}
                  {detectionError && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-red-900/70 px-4 text-center text-white">
                      <p className="text-sm font-semibold">Unable to run detection</p>
                      <p className="mt-1 text-xs opacity-90">
                        {detectionError?.message || "Refresh this page to try again."}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="text-center text-white">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
                    <p className="text-sm">Waiting for camera feed...</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Positioning Checks Card */}
          <div className="bg-white rounded-[10px] border border-[#0000001A] p-5 shadow-sm">
            <h3 className="text-base font-ubuntu-sans font-medium text-[#414447] leading-[20px] mb-4">
              Real-Time Positioning Checks
            </h3>

            <div className="flex flex-col gap-3 mb-4">
              <div className="flex items-center gap-3">
                <div
                  className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    laptopDetected
                      ? "border-[#00C950] bg-[#00C950]"
                      : "border-[#C4C4C4] bg-white"
                  }`}
                >
                  {laptopDetected && (
                    <CheckCircle2
                      className="w-3 h-3 text-white"
                      strokeWidth={3}
                    />
                  )}
                </div>
                <span className="text-sm text-[#686B6D] leading-[20px]">
                  Clear view of laptop screen
                </span>
              </div>
              <div className="flex items-center gap-3">
                <div
                  className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    handsDetected
                      ? "border-[#00C950] bg-[#00C950]"
                      : "border-[#C4C4C4] bg-white"
                  }`}
                >
                  {handsDetected && (
                    <CheckCircle2
                      className="w-3 h-3 text-white"
                      strokeWidth={3}
                    />
                  )}
                </div>
                <span className="text-sm text-[#686B6D] leading-[20px]">
                  Your hands visible on keyboard
                </span>
              </div>
            </div>



            <button
              onClick={() => {
                if (!hasProceeded) {
                  onProceed();
                }
              }}
              disabled={!isReadyToProceed || hasProceeded}
              className={`w-full h-10 rounded-lg text-sm font-medium transition-colors leading-[20px] ${
                isReadyToProceed && !hasProceeded
                  ? "bg-[#030213] text-white hover:bg-[#030213ef]"
                  : hasProceeded
                  ? "bg-[#DCFCE7] text-[#0F172A] cursor-default"
                  : "bg-[#E0E0DF] text-[#9E9E9E] cursor-not-allowed"
              }`}
              type="button"
            >
              {proceedButtonLabel}
            </button>
          </div>
        </div>
    </div>
  );
};

export default MobileWebCamAfterConnection;
